import React, { useState } from 'react';
import { Truck, Clock, AlertCircle, CheckCircle, Calendar, TrendingUp } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface StockItem {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  dailyDemand: number;
  leadTimeDays: number;
  reorderPoint: number;
  orderQuantity: number;
  stockDays: number;
  status: 'optimal' | 'low' | 'critical' | 'excess';
}

interface LeadTimeManagerProps {
  stockItems: StockItem[];
}

interface Supplier {
  id: string;
  name: string;
  categories: string[];
  averageLeadTime: number;
  reliability: number; // 0-100
  minOrderAmount: number;
  deliveryDays: string[];
}

const LeadTimeManager: React.FC<LeadTimeManagerProps> = ({ stockItems }) => {
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  
  // サプライヤー情報
  const suppliers: Supplier[] = [
    {
      id: 'SUP001',
      name: '東京食品卸',
      categories: ['飲料', '菓子', '日配品'],
      averageLeadTime: 2,
      reliability: 95,
      minOrderAmount: 50000,
      deliveryDays: ['月', '水', '金']
    },
    {
      id: 'SUP002',
      name: '関東冷凍センター',
      categories: ['冷凍食品'],
      averageLeadTime: 3,
      reliability: 88,
      minOrderAmount: 30000,
      deliveryDays: ['火', '木']
    },
    {
      id: 'SUP003',
      name: '新鮮野菜直送',
      categories: ['生鮮'],
      averageLeadTime: 1,
      reliability: 92,
      minOrderAmount: 20000,
      deliveryDays: ['毎日']
    },
    {
      id: 'SUP004',
      name: '全国飲料配送',
      categories: ['飲料'],
      averageLeadTime: 2,
      reliability: 98,
      minOrderAmount: 40000,
      deliveryDays: ['月', '火', '木', '金']
    }
  ];

  // カテゴリ別リードタイム分析
  const categoryLeadTimes = stockItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = {
        items: [],
        avgLeadTime: 0,
        maxLeadTime: 0,
        minLeadTime: Infinity
      };
    }
    acc[item.category].items.push(item);
    acc[item.category].avgLeadTime += item.leadTimeDays;
    acc[item.category].maxLeadTime = Math.max(acc[item.category].maxLeadTime, item.leadTimeDays);
    acc[item.category].minLeadTime = Math.min(acc[item.category].minLeadTime, item.leadTimeDays);
    return acc;
  }, {} as Record<string, any>);

  // 平均を計算
  Object.keys(categoryLeadTimes).forEach(cat => {
    categoryLeadTimes[cat].avgLeadTime /= categoryLeadTimes[cat].items.length;
  });

  // リードタイムリスクの計算
  const calculateRisk = (item: StockItem): 'high' | 'medium' | 'low' => {
    const daysUntilStockout = item.currentStock / item.dailyDemand;
    if (daysUntilStockout <= item.leadTimeDays) return 'high';
    if (daysUntilStockout <= item.leadTimeDays * 1.5) return 'medium';
    return 'low';
  };

  const riskItems = stockItems.map(item => ({
    ...item,
    risk: calculateRisk(item),
    daysUntilStockout: Math.floor(item.currentStock / item.dailyDemand)
  }));

  const highRiskCount = riskItems.filter(i => i.risk === 'high').length;
  const mediumRiskCount = riskItems.filter(i => i.risk === 'medium').length;

  return (
    <div className="space-y-6">
      {/* リードタイムリスクサマリー */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <AlertCircle size={20} style={{ color: colors.status.error }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {highRiskCount}品
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                高リスク
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Clock size={20} style={{ color: colors.status.warning }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {mediumRiskCount}品
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                中リスク
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Truck size={20} style={{ color: colors.accent.blue }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                2.3日
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                平均リードタイム
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} style={{ color: colors.support.success }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                93.5%
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                納期遵守率
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* サプライヤー管理 */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            サプライヤー管理
          </h3>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            {suppliers.map(supplier => (
              <div 
                key={supplier.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                style={{ 
                  borderColor: selectedSupplier === supplier.id ? colors.accent.blue : colors.ui.border,
                  backgroundColor: selectedSupplier === supplier.id ? colors.accent.blue + '10' : 'transparent'
                }}
                onClick={() => setSelectedSupplier(supplier.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium" style={{ color: colors.text.primary }}>
                      {supplier.name}
                    </h4>
                    <div className="flex gap-2 mt-1">
                      {supplier.categories.map(cat => (
                        <Badge key={cat} variant="secondary" size="small">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ color: colors.accent.blue }}>
                      {supplier.averageLeadTime}日
                    </div>
                    <div className="text-xs" style={{ color: colors.text.secondary }}>
                      リードタイム
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: colors.text.secondary }}>信頼性</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all"
                          style={{ 
                            width: `${supplier.reliability}%`,
                            backgroundColor: supplier.reliability > 90 ? colors.support.success : 
                                          supplier.reliability > 80 ? colors.status.warning : 
                                          colors.status.error
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium">{supplier.reliability}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: colors.text.secondary }}>最小発注額</span>
                    <span className="font-medium">¥{supplier.minOrderAmount.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: colors.text.secondary }}>配送日</span>
                    <span className="font-medium">{supplier.deliveryDays.join('・')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* カテゴリ別リードタイム分析 */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            カテゴリ別リードタイム分析
          </h3>
        </div>
        
        <div className="p-4">
          <div className="space-y-3">
            {Object.entries(categoryLeadTimes).map(([category, data]) => (
              <div key={category} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium" style={{ color: colors.text.primary }}>
                    {category}
                  </span>
                  <div className="flex items-center gap-4 text-sm">
                    <span style={{ color: colors.text.secondary }}>
                      平均: <strong>{data.avgLeadTime.toFixed(1)}日</strong>
                    </span>
                    <span style={{ color: colors.text.secondary }}>
                      範囲: {data.minLeadTime}-{data.maxLeadTime}日
                    </span>
                  </div>
                </div>
                
                <div className="h-8 bg-gray-100 rounded relative">
                  {data.items.slice(0, 10).map((item: StockItem, idx: number) => {
                    const risk = calculateRisk(item);
                    return (
                      <div
                        key={item.skuId}
                        className="absolute top-1 h-6 w-6 rounded-full border-2 border-white"
                        style={{
                          left: `${(idx / 10) * 95}%`,
                          backgroundColor: risk === 'high' ? colors.status.error :
                                        risk === 'medium' ? colors.status.warning :
                                        colors.support.success
                        }}
                        title={`${item.skuName}: ${item.leadTimeDays}日`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* リードタイムリスク品目 */}
      <Card>
        <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
          <h3 className="font-semibold" style={{ color: colors.text.primary }}>
            リードタイムリスク品目
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b" style={{ borderColor: colors.ui.border }}>
              <tr style={{ backgroundColor: colors.ui.bgSecondary }}>
                <th className="text-left p-3 text-sm font-medium">SKU</th>
                <th className="text-center p-3 text-sm font-medium">現在庫</th>
                <th className="text-center p-3 text-sm font-medium">日次需要</th>
                <th className="text-center p-3 text-sm font-medium">在庫切れまで</th>
                <th className="text-center p-3 text-sm font-medium">リードタイム</th>
                <th className="text-center p-3 text-sm font-medium">リスク</th>
                <th className="text-center p-3 text-sm font-medium">推奨アクション</th>
              </tr>
            </thead>
            <tbody>
              {riskItems
                .filter(item => item.risk !== 'low')
                .slice(0, 10)
                .map(item => (
                  <tr key={item.skuId} className="border-b hover:bg-gray-50" style={{ borderColor: colors.ui.border }}>
                    <td className="p-3">
                      <div>
                        <div className="text-sm font-medium">{item.skuId}</div>
                        <div className="text-xs text-gray-500">{item.skuName}</div>
                      </div>
                    </td>
                    <td className="p-3 text-center">{item.currentStock}</td>
                    <td className="p-3 text-center">{item.dailyDemand}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Calendar size={14} style={{ color: colors.text.secondary }} />
                        <span className={item.daysUntilStockout <= item.leadTimeDays ? 'text-red-600 font-bold' : ''}>
                          {item.daysUntilStockout}日
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Truck size={14} style={{ color: colors.text.secondary }} />
                        <span>{item.leadTimeDays}日</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge 
                        variant={item.risk === 'high' ? 'error' : 'warning'}
                      >
                        {item.risk === 'high' ? '高' : '中'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <Button size="small" variant={item.risk === 'high' ? 'primary' : 'secondary'}>
                        {item.risk === 'high' ? '緊急発注' : '発注検討'}
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default LeadTimeManager;
